import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, Checkbox, FormControlLabel } from "@mui/material";

export type LicenseFields = {
    spdx_id: string;
    full_name?: string;
    text_url?: string;
    license_text?: string;
    is_approved: boolean;
};

export type License = LicenseFields & {
    id: number;
};

export type LicenseUpdate = Partial<LicenseFields>;

interface LicenseDialogProps {
    open: boolean;
    mode: 'add' | 'edit';
    license?: License | null;
    onSubmit: (license: any) => void;
    onClose: () => void;
}

export const LicenseDialog = ({ open, mode, license, onSubmit, onClose }: LicenseDialogProps) => {
    const [spdxId, setSpdxId] = useState("");
    const [fullName, setFullName] = useState("");
    const [textUrl, setTextUrl] = useState("");
    const [licenseText, setLicenseText] = useState("");
    const [isApproved, setIsApproved] = useState(false);

    useEffect(() => {
        if (license) {
            setSpdxId(license.spdx_id || "");
            setFullName(license.full_name || "");
            setTextUrl(license.text_url || "");
            setLicenseText(license.license_text || "");
            setIsApproved(license.is_approved || false);
        }
    }, [license]);

    const reset = () => {
        setSpdxId("");
        setFullName("");
        setTextUrl("");
        setLicenseText("");
        setIsApproved(false);
    };

    const getFormData = () => ({
        spdx_id: spdxId,
        full_name: fullName,
        text_url: textUrl,
        license_text: licenseText,
        is_approved: isApproved
    });

    const closeAndReset = () => {
        reset();
        onClose();
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (mode === 'edit' && license) {
            onSubmit({
                ...license,
                ...getFormData()
            });
        } else {
            onSubmit(getFormData());
        }

        closeAndReset();
    };

    const title = mode === 'add' ? 'Add License' : 'Edit License';
    const submitButtonText = mode === 'add' ? 'Create' : 'Save';

    return (
        <Dialog open={open} onClose={closeAndReset} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="SPDX ID"
                            value={spdxId}
                            onChange={e => setSpdxId(e.target.value)}
                            required
                        />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Full Name"
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                        />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Text URL"
                            value={textUrl}
                            onChange={e => setTextUrl(e.target.value)}
                        />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="License Text"
                            value={licenseText}
                            onChange={e => setLicenseText(e.target.value)}
                            multiline
                            rows={4}
                        />
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isApproved}
                                onChange={e => setIsApproved(e.target.checked)}
                            />
                        }
                        label="Approved"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAndReset}>Cancel</Button>
                    <Button type="submit" variant="contained">{submitButtonText}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};